import { useEffect, useState } from 'react';
import { askRematch, friendLink, leaveOnline, quickMatch } from '../game/online';
import { game } from '../game/controller';
import { useGame } from '../game/store';
import { copyText } from './share';

/** Countdown number that ticks down to the start. */
function Countdown({ until }: { until: number }) {
  const [now, setNow] = useState(performance.now());
  useEffect(() => {
    const id = setInterval(() => setNow(performance.now()), 100);
    return () => clearInterval(id);
  }, []);
  const s = Math.max(0, Math.ceil((until - now) / 1000));
  return <div className="countdown">{s > 0 ? s : 'GO'}</div>;
}

/** Connecting, waiting for an opponent or a friend, the countdown, and connection errors. */
export function OnlineLobby() {
  const o = useGame((s) => s.online);
  const tier = useGame((s) => s.tier);
  const [copied, setCopied] = useState(false);
  if (!['connecting', 'waiting', 'countdown', 'error'].includes(o.status)) return null;
  const link = o.code ? friendLink(o.code) : '';
  return (
    <div className="overlay lobby-overlay" role="dialog" aria-modal="true" aria-labelledby="lobby-title">
      <div className="card lobby">
        <div className="eyebrow">Online 1v1</div>
        {o.status === 'connecting' && <h2 id="lobby-title">Connecting…</h2>}
        {o.status === 'waiting' && !o.code && (
          <>
            <h2 id="lobby-title">Looking for an opponent…</h2>
            <p>You'll be matched with the next person who joins. It's early days, so it may take a while: invite a friend, or play the bot while you wait.</p>
            <div className="searching" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
          </>
        )}
        {o.status === 'waiting' && o.code && (
          <>
            <h2 id="lobby-title">Send this to a friend</h2>
            <p>When they open the link, you'll both drop into the same fresh cube.</p>
            <div className="invite">
              <code>{link.replace(/^https?:\/\//, '')}</code>
            </div>
            <div className="row">
              <button
                className="cta"
                type="button"
                onClick={async () => {
                  const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
                  if (nav.share) {
                    try {
                      await nav.share({ title: 'QOOBD', text: 'Hunt me in QOOBD: one cube, one rival.', url: link });
                      return;
                    } catch {
                      /* fall back to copying */
                    }
                  }
                  setCopied(await copyText(link));
                }}
              >
                {copied ? 'Link copied' : 'Share link'}
              </button>
            </div>
            <p className="note">Waiting for your friend to join…</p>
          </>
        )}
        {o.status === 'countdown' && (
          <>
            <h2 id="lobby-title">Opponent found</h2>
            <Countdown until={o.startsAt} />
            <p className="note">A fresh cube. You both start the same distance from the core.</p>
          </>
        )}
        {o.status === 'error' && (
          <>
            <h2 id="lobby-title" className="lose">
              Can't connect
            </h2>
            <p>{o.error}</p>
          </>
        )}
        <div className="row">
          {o.status === 'error' && (
            <button className="cta" type="button" onClick={() => void quickMatch()}>
              Try again
            </button>
          )}
          {o.status === 'waiting' && !o.code && (
            <button
              className="cta alt"
              type="button"
              onClick={() => {
                void leaveOnline();
                game.start(tier);
              }}
            >
              Play the bot instead
            </button>
          )}
          {o.status !== 'countdown' && (
            <button
              className="cta alt"
              type="button"
              onClick={() => {
                void leaveOnline();
                if (useGame.getState().phase === 'over') useGame.setState({ phase: 'intro', mode: 'play' });
              }}
            >
              {o.status === 'error' ? 'Close' : 'Cancel'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** A banner shown in the match while the opponent's connection is down. */
export function OpponentAway() {
  const away = useGame((s) => s.online.opponentAway && s.mode === 'online' && s.phase === 'playing');
  if (!away) return null;
  return (
    <div className="hud away" role="status">
      Opponent disconnected · waiting up to 20 s for them to return
    </div>
  );
}

/** Results-screen buttons for an online match. */
export function OnlineResultActions() {
  const o = useGame((s) => s.online);
  const gone = useGame((s) => s.result?.forfeit === true);
  return (
    <div className="row">
      {!gone && (
        <button className="cta" type="button" disabled={o.rematchSent || o.status !== 'over'} onClick={askRematch}>
          {o.rematchSent ? 'Waiting for opponent…' : o.rematchAsked ? 'Accept rematch' : 'Rematch'}
        </button>
      )}
      <button className="cta alt" type="button" onClick={() => void quickMatch()}>
        New opponent
      </button>
      <button
        className="cta alt"
        type="button"
        onClick={() => {
          void leaveOnline();
          useGame.setState({ phase: 'intro', mode: 'play' });
        }}
      >
        Back to title
      </button>
    </div>
  );
}
